<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import LoginForm from '@/components/authentication/forms/login-form';
import RegisterForm from '@/components/authentication/forms/register-form';

export default {
  props: {
    jobId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      authType: 'login',
      importComment: 'registerInterest',
      validateAll: false,
      ui: {
        loading: false,
      },
      reAuthenticationSuccessMessage: '',
      serverValidationErrors: {},
      diversityQuestions: ['ethnicity', 'free_school_meals', 'parent_profession'],
    };
  },
  computed: {
    ...mapState('user', {
      user: (state) => state,
    }),

    ...mapGetters('savedItems', {
      getItems: 'getItems',
    }),

    ...mapState('entities', {
      genders: (state) => state.genders,
      courses: (state) => state.courses,
      startYears: (state) => state.startYears,
      endYears: (state) => state.endYears,
      universities: (state) => state.universities, // RMA ?
      jobTypes: (state) => state.jobTypes,
      jobRoles: (state) => state.jobRoles,
    }),

    ...mapGetters('user', {
      getIncompleteQuestions: 'getIncompleteQuestions',
    }),

    questionsToComplete() {
      // Currently we do not include diversity questions in the RYI form so remove them from questions to complete
      return this.getIncompleteQuestions.filter((question) => this.diversityQuestions.indexOf(question) === -1);
    },

    authenticationComponent: {
      get() {
        return this.authType === 'login' ? LoginForm : RegisterForm;
      },
      set(type) {
        this.authType = type;
      },
    },

    isItemShortlisted() {
      return this.getItems.filter((item) => item.id === this.jobId).length > 0;
    },
  },

  watch: {
    user: {
      handler() {
        if (this.ui.loading) return;

        this.ui.loading = true;

        this.loadEntities();

        this.getSavedItems().then(() => {
          this.ui.loading = false;
        });
      },
      deep: true,
    },
  },

  mounted() {
    if (this.user.loggedIn) {
      this.loadEntities();
    }

    this.$bus.$on('bus:register-form:success', this.scrollFormIntoView);
    this.$bus.$on('bus:login-form:success', this.scrollFormIntoView);
  },

  beforeDestroy() {
    this.$bus.$off('bus:register-form:success', this.scrollFormIntoView);
    this.$bus.$off('bus:login-form:success', this.scrollFormIntoView);
  },

  methods: {
    ...mapActions('user', [
      'updateUser',
    ]),

    ...mapActions('savedItems', {
      addItem: 'addItem',
      deleteItem: 'deleteItem',
      getSavedItems: 'getSavedItems',
    }),

    ...mapActions('entities', {
      getCourses: 'getCourses',
      getJobRoles: 'getJobRoles',
      getJobTypes: 'getJobTypes',
      getGenders: 'getGenders',
    }),

    handleSubmit({ userPayload }) {
      this.ui.loading = true;
      this.validateAll = true;

      if (this.questionsToComplete.length > 0) {
        this.updateProfileAndRegister({ userPayload });
        return false;
      }

      // handle register interest post
      this.registerInterest();
      return false;
    },

    handleSuccess() {
      this.ui.loading = false;
    },

    handleError() {
      this.ui.loading = false;
      this.$bus.$emit('bus:base-alert', {
        type: 'Error',
        message: this.$lang.alert.error.heading,
      });
    },

    registerInterest() {
      this.scrollFormIntoView();
      this.addItem({ id: this.jobId, type: 'jobs' })
        .then(this.handleSuccess)
        .catch(this.handleError);
    },

    unregisterInterest() {
      this.ui.loading = true;

      this.deleteItem({ id: this.jobId, type: 'jobs' })
        .then(this.handleSuccess)
        .catch(this.handleError);
    },

    scrollFormIntoView() {
      const element = document.querySelector('#vue-register-interest-form');
      if (this.scrollTo !== null) {
        const { position, top } = element.style;
        // required for chrome that ignores sticky header offset
        element.style.position = 'relative';
        element.style.top = '-100px';
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
        element.style.top = top;
        element.style.position = position;
      }
    },

    updateProfileAndRegister({ userPayload }) {
      this.updateUser(userPayload)
        .then(this.registerInterest)
        .catch(this.handleError);
    },

    showValidationErrors() {
      this.validateAll = true;
    },

    setAuthenticationComponent(component) {
      this.authenticationComponent = component;
    },

    loadEntities() {
      if (this.questionsToComplete.indexOf('gender') > -1) {
        this.getGenders();
      }

      // RMA ?
      if (this.questionsToComplete.indexOf('degree_discipline') > -1) {
        this.getCourses();
      }

      if (this.questionsToComplete.indexOf('jobTypes') > -1) {
        this.getJobTypes();
      }

      if (this.questionsToComplete.indexOf('jobRoles') > -1) {
        this.getJobRoles();
      }
    },
  },

  render() {
    const slot = this.$slots.default({
      props: {
        reAuthenticationSuccessMessage: this.reAuthenticationSuccessMessage,
        authType: this.authType,
        user: this.user,
        importComment: this.importComment,
        authenticationComponent: this.authenticationComponent,
        questionsToComplete: this.questionsToComplete,
        loading: this.ui.loading,
        validateAll: this.validateAll,
        serverValidationErrors: this.serverValidationErrors,
        isItemShortlisted: this.isItemShortlisted,
        entities: {
          genders: this.genders,
          courses: this.courses,
          startYears: this.startYears,
          endYears: this.endYears,
          universities: this.universities,
          jobTypes: this.jobTypes,
          jobRoles: this.jobRoles[0],
        },
      },
      listeners: {
        setAuthenticationComponent: this.setAuthenticationComponent,
        handleSubmit: this.handleSubmit,
        unregisterInterest: this.unregisterInterest,
        showValidationErrors: this.showValidationErrors,
        scrollFormIntoView: this.scrollFormIntoView,
      },
    });

    return Array.isArray(slot) ? slot[0] : slot;
  },
};
</script>
